import { useState } from "react";
import { ArrowRight, ChevronRight, Radio } from "lucide-react";
import { ThemeToggle } from "./theme";
import { mount } from "./mount";
import { Card, Logo, PrimaryButton, PulseDot, TextInput } from "./ui";
import { track } from "./lib";
import "./styles.css";

function loadStations(): string[] {
  try {
    const saved = localStorage.getItem("stations");
    return saved ? (JSON.parse(saved) as string[]) : [];
  } catch {
    return [];
  }
}

function launch(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return;
  track("create-stream", { station: trimmed });
  window.location.href = "/s/" + encodeURIComponent(trimmed);
}

function RecentStations({ stations }: { stations: string[] }) {
  if (stations.length === 0) return null;
  return (
    <div className="mt-8">
      <h2 className="mb-3 text-xs font-medium uppercase tracking-widest text-zinc-400 dark:text-zinc-500">
        Recent stations
      </h2>
      <div className="space-y-1">
        {stations.map((s) => (
          <a
            key={s}
            href={"/s/" + encodeURIComponent(s)}
            data-umami-event="visit-recent-stream"
            data-umami-event-station={s}
            className="group flex items-center justify-between rounded-lg border px-4 py-3 transition-colors border-zinc-200 bg-white/60 text-zinc-700 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-200 dark:hover:border-zinc-700 dark:hover:bg-zinc-900"
          >
            <span className="flex min-w-0 items-center gap-3">
              <Radio className="h-4 w-4 shrink-0 text-emerald-500" />
              <span className="truncate text-sm font-medium">{s}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400 transition-colors group-hover:text-zinc-700 dark:text-zinc-600 dark:group-hover:text-zinc-300" />
          </a>
        ))}
      </div>
    </div>
  );
}

function Landing() {
  const [name, setName] = useState("");
  const [stations] = useState(loadStations);

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-6">
      <header className="flex items-center justify-between py-6">
        <Logo />
        <ThemeToggle />
      </header>

      <main className="flex flex-1 flex-col justify-center py-10">
        <div className="mb-10">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs border-zinc-200 bg-white/60 text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-400">
            <PulseDot />
            Shared, synced, always live
          </div>
          <h1 className="text-5xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-50 sm:text-6xl">
            Your own radio <span className="text-emerald-500">station.</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-zinc-600 dark:text-zinc-400">
            Name a station, drop in YouTube links or audio files, and everyone
            tuned in hears the same thing at the same time. Share one URL — it
            plays anywhere.
          </p>
        </div>

        <Card className="p-6 sm:p-6">
          <label
            htmlFor="name_field"
            className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300"
          >
            Station name
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Radio className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
              <TextInput
                id="name_field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && launch(name)}
                className="py-3 pl-9"
                placeholder="late-night-coding"
                autoComplete="off"
              />
            </div>
            <PrimaryButton onClick={() => launch(name)} className="px-5 py-3">
              Launch station
              <ArrowRight className="h-4 w-4" />
            </PrimaryButton>
          </div>
          <p className="mt-3 text-xs text-zinc-400 dark:text-zinc-600">
            Anyone with the name can join and add to the queue.
          </p>
        </Card>

        <RecentStations stations={stations} />
      </main>

      <footer className="flex items-center justify-between py-6 text-xs text-zinc-400 dark:text-zinc-700">
        <span>Streamwave</span>
        <a
          href="/analytics"
          className="transition-colors hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          Analytics
        </a>
      </footer>
    </div>
  );
}

mount(<Landing />);
